import React, { Fragment, useCallback, useState, MouseEvent } from "react";
import { Box, ActivityIndicator, EmptyState, Button, OptionCard } from "@thenounproject/lingo-core";

import ModalHeader from "../../../../app/_components/ModalHeader";
import ModalBody from "../../../../app/_components/ModalBody";
import ModalFooter from "../../../../app/_components/ModalFooter";

import useMarkets from "@redux/actions/markets/useMarkets";
import { useSelectSpace } from "@selectors/entities/spaces";
import useModals, { ModalTypes } from "@redux/actions/useModals";

type Props = {
  dismissModal?: (e?: MouseEvent | KeyboardEvent) => void;
  currentMarketUuids: string[];
  onAssign: (uuids: string[]) => void;
};

const AssignProductMarketModal: React.FC<Props> = ({
  dismissModal,
  currentMarketUuids,
  onAssign,
}) => {
  const space = useSelectSpace();
  const { showModal } = useModals();
  const [selectedUuids, setSelectedUuids] = useState<string[]>(currentMarketUuids);

  const { data: marketsData, isLoading, error: loadError } = useMarkets({ spaceId: space.id });
  const markets = marketsData?.markets || [];

  const handleCheckboxClick = useCallback((marketUuid: string) => {
    setSelectedUuids(prev =>
      prev.includes(marketUuid) ? prev.filter(u => u !== marketUuid) : [...prev, marketUuid]
    );
  }, []);

  const handleSave = useCallback(() => {
    onAssign(selectedUuids);
    dismissModal?.();
  }, [onAssign, selectedUuids, dismissModal]);

  const handleAddMarket = useCallback(() => {
    showModal(ModalTypes.ADD_MARKET, {
      onSuccess: (uuid: string) => {
        setSelectedUuids(prev => [...prev, uuid]);
      },
    });
  }, [showModal]);

  const createMarketButtonProps = {
    text: "Create a market",
    onClick: handleAddMarket,
    buttonStyle: "tertiary" as const,
    size: "small" as const,
  };

  function renderModalBody() {
    if (isLoading) {
      return <ActivityIndicator center />;
    }
    if (loadError) {
      return (
        <EmptyState size="small" title="Unable to load markets" subtitle={loadError.message} />
      );
    }
    return (
      <Box>
        {markets.length > 0 ? (
          <Box>
            {markets.map(market => (
              <OptionCard
                key={market.uuid}
                title={market.name}
                cardStyle="checkbox"
                size="small"
                selected={selectedUuids.includes(market.uuid)}
                onClick={() => handleCheckboxClick(market.uuid)}
              />
            ))}
          </Box>
        ) : (
          <EmptyState
            size="small"
            title="No markets yet"
            subtitle="Create your first market to get started."
            styleOverrides={{ py: "none" }}
            button={createMarketButtonProps}
          />
        )}
        {markets.length > 0 && (
          <Button {...createMarketButtonProps} mt="m" display="block" mx="auto" />
        )}
      </Box>
    );
  }

  return (
    <Fragment>
      <ModalHeader title="Add Markets" />
      <ModalBody>{renderModalBody()}</ModalBody>
      <ModalFooter
        primary={{
          text: "Done",
          onClick: handleSave,
        }}
      />
    </Fragment>
  );
};

export default AssignProductMarketModal;
